import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { AccessRight } from '../models/oAuth.model';
import { VersionType } from '../services/access-matrix.service';
import { Credentials } from '../models/credentials.model';
import {
  FirestoreAccessRightDocument,
  FirestoreConfigDocument,
} from './persistence.model';

@Injectable({
  providedIn: 'root',
})
export class PersistenceService {
  private readonly firestore = inject(Firestore);

  public async getAccessRight(
    userEmail: string,
  ): Promise<AccessRight | undefined> {
    const document = await this.getDocument(userEmail);
    if (!document) {
      return undefined;
    }

    // Use former model to keep the rest of the application compatible
    // By adding the VersionType available, we authorized all valid users to
    // both environment (FULL or FREE)
    return {
      user: userEmail,
      rights: [
        {
          version: VersionType.FULL,
          options: this.getOptions(document),
        },
        {
          version: VersionType.FREE,
          options: this.getOptions(document),
        },
      ],
    };
  }

  public async getCredentials(
    type: VersionType,
  ): Promise<Credentials | undefined> {
    const document = await this.getConfigDocument();

    let username: string | undefined = undefined;
    let password: string | undefined = undefined;

    if (type === VersionType.FULL) {
      username = document?.icureFullUsername;
      password = document?.icureFullPassword;
    }

    if (type === VersionType.FREE) {
      username = document?.icureFreeUsername;
      password = document?.icureFreePassword;
    }

    if (!username || !password) {
      return undefined;
    }

    const credentials: Credentials = new Credentials({
      username,
      password,
    });

    return credentials;
  }

  private async getDocument(
    userEmail: string,
  ): Promise<FirestoreAccessRightDocument | undefined> {
    const userId = encodeURIComponent(userEmail);
    const documentRef = doc(this.firestore, 'users', userId);
    const snapshot = await getDoc(documentRef);

    if (!snapshot.exists()) {
      return undefined;
    }

    return snapshot.data() as FirestoreAccessRightDocument;
  }

  private async getConfigDocument(): Promise<
    FirestoreConfigDocument | undefined
  > {
    const documentRef = doc(this.firestore, 'config', 'secrets');
    const snapshot = await getDoc(documentRef);

    if (!snapshot.exists()) {
      return undefined;
    }

    return snapshot.data() as FirestoreConfigDocument;
  }

  private getOptions(accessRight: FirestoreAccessRightDocument): string[] {
    const options: string[] = [];

    if (accessRight.privilegeGroupCreation === true) {
      options.push('groupCreation');
    }

    if (accessRight.privilegeBatchWriteAction === true) {
      options.push('batchWriteAction');
    }

    return options;
  }
}
